import type {
  AgentRuntimeActionOutcome,
  AgentRuntimeActiveEditOperation,
  AgentRuntimeInteractionRequest,
  AgentRuntimeStatus,
  AgentType,
} from "@cord/shared"
import { reconcileRuntimeSnapshot } from "@/lib/runtime-snapshot-stability"
import { useAgentRuntime } from "@/stores/agent-runtime"
import type { ActiveEditMessageOperation } from "@/hooks/thread-sync-client-types"

export function patchActiveRuntimeSnapshot(input: {
  activeEditOperation?: AgentRuntimeActiveEditOperation | ActiveEditMessageOperation | null
  sessionActionOutcome?: AgentRuntimeActionOutcome | null
}): void {
  const store = useAgentRuntime.getState()
  if (!store.snapshot) return
  const nextSnapshot = {
    ...store.snapshot,
    activeEditOperation:
      input.activeEditOperation === undefined
        ? store.snapshot.activeEditOperation
        : (input.activeEditOperation as AgentRuntimeActiveEditOperation | null),
    sessionActionOutcome:
      input.sessionActionOutcome === undefined
        ? store.snapshot.sessionActionOutcome
        : input.sessionActionOutcome,
  }
  useAgentRuntime.setState((state) => ({
    snapshot: reconcileRuntimeSnapshot(store.snapshot, nextSnapshot),
    stateVersion: state.stateVersion + 1,
  }))
}

export function applyControlPlaneSnapshotToActiveRuntime(args: {
  agentType: AgentType
  pendingInteractions: AgentRuntimeInteractionRequest[]
  status: AgentRuntimeStatus
  conversationEpoch: number
  activeEditOperation: ActiveEditMessageOperation | null
}): void {
  useAgentRuntime.getState().applyControlPlaneSnapshot({
    agentType: args.agentType,
    pendingInteractions: args.pendingInteractions,
    status: args.status,
    conversationEpoch: args.conversationEpoch,
  })

  patchActiveRuntimeSnapshot({
    activeEditOperation: args.activeEditOperation,
  })
}

function normalizeNonNegativeInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return null
  }

  return Math.floor(value)
}

export function advanceActiveRuntimeCursor(args: {
  nextCursor: number
}): void {
  const normalizedNextCursor = normalizeNonNegativeInteger(args.nextCursor)
  if (normalizedNextCursor === null) {
    return
  }

  useAgentRuntime.setState((state) => {
    const currentLastEventId = normalizeNonNegativeInteger(state.lastEventId)
    const nextLastEventId = Math.max(currentLastEventId ?? 0, normalizedNextCursor)
    const currentProviderEventId = normalizeNonNegativeInteger(
      state.snapshot?.cursorState.providerEventId,
    )
    const nextProviderEventId = Math.max(
      currentProviderEventId ?? 0,
      normalizedNextCursor,
    )
    const nextSnapshot = state.snapshot
      ? currentProviderEventId === nextProviderEventId
        ? state.snapshot
        : {
            ...state.snapshot,
            cursorState: {
              ...state.snapshot.cursorState,
              providerEventId: nextProviderEventId,
            },
          }
      : state.snapshot

    if (
      nextLastEventId === currentLastEventId &&
      nextSnapshot === state.snapshot
    ) {
      return state
    }

    return {
      lastEventId: nextLastEventId,
      snapshot: nextSnapshot,
      stateVersion: state.stateVersion + 1,
    }
  })
}
